import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import React from 'react'

// Route-initials avatar wrapped in a completion ring — a port of the
// prototype's AvatarRing. The ring draws proportional work-order-status arcs
// (serviced / skipped / hold / canceled) over a neutral scheduled-remainder
// track, so the unfilled portion reads as "still scheduled". Segments arrive
// already in draw order as { key, color, value }; only value>0 arcs draw, with
// a hairline gap between neighbours.
export interface RingSegment {
  key: string
  color: string
  value: number
}

interface AvatarRingProps {
  initial: string
  color: string
  segments: RingSegment[]
  total: number
  size?: number
}

// The ring is the tallest thing in a sheet row, so the row height reads it.
export const AVATAR_RING_SIZE = 40
const STROKE = 3
// How much of the ring the avatar leaves showing, so the arcs stay legible at
// every size.
const AVATAR_INSET = 10

export const AvatarRing = ({ initial, color, segments, total, size = AVATAR_RING_SIZE }: AvatarRingProps) => {
  const theme = useTheme()
  const track = theme.palette.divider
  const radius = (size - STROKE) / 2
  const circumference = 2 * Math.PI * radius

  const active = segments.filter(segment => segment.value > 0)
  const gap = active.length > 1 ? 2 : 0
  const arcs = active.map(segment => (total > 0 ? (segment.value / total) * circumference : 0))
  const drawn = active.map((segment, index) => ({
    key: segment.key,
    color: segment.color,
    length: Math.max(0, arcs[index] - gap),
    // Half-gap start + the sum of all prior arcs (prefix sum).
    offset: gap / 2 + arcs.slice(0, index).reduce((sum, arc) => sum + arc, 0),
  }))

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      {total > 0 && (
        <Box component="svg" height={size} sx={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }} width={size}>
          <circle cx={size / 2} cy={size / 2} fill="none" r={radius} stroke={track} strokeWidth={STROKE} />
          {drawn.map(segment => (
            <circle
              key={segment.key}
              cx={size / 2}
              cy={size / 2}
              fill="none"
              r={radius}
              stroke={segment.color}
              strokeDasharray={`${segment.length} ${circumference}`}
              strokeDashoffset={-segment.offset}
              strokeLinecap="butt"
              strokeWidth={STROKE}
            />
          ))}
        </Box>
      )}
      <Avatar
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size - AVATAR_INSET,
          height: size - AVATAR_INSET,
          bgcolor: color,
          fontSize: Math.round(size * 0.3),
          fontWeight: 700,
        }}
      >
        {initial}
      </Avatar>
    </Box>
  )
}
